import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useDocumentHistory } from "~/api/v2/useDocumentHistory";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from "~/atoms/item";
import { Skeleton } from "~/atoms/skeleton";
import NoResult from "./NoResult";
import AvatarWithFallback from "./AvatarWithFallback";
import { formatDate } from "~/helpers/date";
import { Badge } from "~/atoms/badge";
import { type DocumentHistoryEvent } from "~/api/types";

function getEventLabel(event: DocumentHistoryEvent, t: TFunction): string {
  return t(($) => $.commons.documentHistory.events[event.type], event.details);
}

interface Props {
  documentId: number;
}

export function DocumentAuditTrailTimeline({ documentId }: Props) {
  const { t } = useTranslation();

  const { data: historyData, isLoading: isLoadingHistory } =
    useDocumentHistory(documentId);

  if (isLoadingHistory) {
    return (
      <div className="space-y-4 xl:w-2/3">
        <Skeleton className="h-16 rounded-xl" />
        <Skeleton className="h-16 rounded-xl" />
        <Skeleton className="h-16 rounded-xl" />
      </div>
    );
  }

  if (!historyData) {
    return <NoResult message={t(($) => $.commons.noItems)} />;
  }

  return (
    <ItemGroup className="max-w-3xl gap-0">
      {historyData.history.map((event, i) => (
        <Item
          key={i}
          variant="outline"
          className="rounded-none border-b-0 first:rounded-t-xl last:rounded-b-xl last:border-b"
        >
          <ItemContent>
            <ItemTitle>{getEventLabel(event, t)}</ItemTitle>
            {event.actor && (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <AvatarWithFallback user={event.actor} size="xs" />
                {event.actor.firstName} {event.actor.lastName}
                {" • "}
                {formatDate(event.timestamp, "dateTime")}
              </div>
            )}
          </ItemContent>
          {event.release && (
            <ItemActions className="self-start">
              <Badge variant="outline">
                {t(($) => $.commons.workflow.versionOrder, {
                  order: event.release.order,
                })}
              </Badge>
            </ItemActions>
          )}
        </Item>
      ))}
    </ItemGroup>
  );
}
